import React, { useRef, useEffect } from 'react'

interface GridGlowBackgroundProps {
  children: React.ReactNode
  backgroundColor?: string
  gridColor?: string
  gridSize?: number
  glowColors?: string[]
  glowCount?: number
  className?: string
}

export const GridGlowBackground: React.FC<GridGlowBackgroundProps> = ({
  children,
  backgroundColor = '#0a0a0a',
  gridColor = 'rgba(255, 255, 255, 0.05)',
  gridSize = 50,
  glowColors = ['var(--electric-blue)', 'var(--electric-purple)', 'var(--electric-blue)'],
  glowCount = 10,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const resolveColor = (c: string) => {
    if (c.startsWith('var(')) {
      const name = c.slice(4, -1).trim()
      return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || '#00d4ff'
    }
    return c
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resolvedGlowColors = glowColors.map(resolveColor)

    let glows: Glow[] = []
    let frameId: number
    let observer: MutationObserver | null = null

    class Glow {
      x: number
      y: number
      targetX: number
      targetY: number
      radius: number
      speed: number
      color: string
      alpha: number

      constructor() {
        this.x = Math.floor(Math.random() * (canvas.width / gridSize)) * gridSize
        this.y = Math.floor(Math.random() * (canvas.height / gridSize)) * gridSize
        this.targetX = this.x
        this.targetY = this.y
        this.radius = Math.random() * 80 + 40
        this.speed = Math.random() * 0.015 + 0.01
        this.color = resolvedGlowColors[Math.floor(Math.random() * resolvedGlowColors.length)]
        this.alpha = 0
        this.setNewTarget()
      }

      setNewTarget() {
        this.targetX = Math.floor(Math.random() * (canvas.width / gridSize)) * gridSize
        this.targetY = Math.floor(Math.random() * (canvas.height / gridSize)) * gridSize
      }

      update() {
        this.x += (this.targetX - this.x) * this.speed
        this.y += (this.targetY - this.y) * this.speed
        if (Math.abs(this.targetX - this.x) < 1 && Math.abs(this.targetY - this.y) < 1) {
          this.setNewTarget()
        }
        if (this.alpha < 1) this.alpha += 0.01
      }

      draw() {
        ctx.globalAlpha = this.alpha
        const grad = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius)
        grad.addColorStop(0, this.color)
        grad.addColorStop(1, 'transparent')
        ctx.fillStyle = grad
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.radius, 0, 2 * Math.PI)
        ctx.fill()
        ctx.globalAlpha = 1
      }
    }

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      glows = Array.from({ length: glowCount }, () => new Glow())
    }

    const drawGrid = () => {
      ctx.strokeStyle = gridColor
      ctx.lineWidth = 1
      for (let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath()
        ctx.moveTo(x, 0)
        ctx.lineTo(x, canvas.height)
        ctx.stroke()
      }
      for (let y = 0; y < canvas.height; y += gridSize) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(canvas.width, y)
        ctx.stroke()
      }
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      drawGrid()
      glows.forEach((g) => {
        g.update()
        g.draw()
      })
      frameId = requestAnimationFrame(animate)
    }

    const rebuildGlows = () => {
      const newColors = glowColors.map(resolveColor)
      glows.forEach((g, i) => {
        g.color = newColors[i % newColors.length]
      })
    }

    observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.attributeName === 'data-theme') {
          rebuildGlows()
        }
      })
    })
    observer.observe(document.documentElement, { attributes: true })

    resize()
    animate()
    window.addEventListener('resize', resize)
    return () => {
      window.removeEventListener('resize', resize)
      cancelAnimationFrame(frameId)
      if (observer) observer.disconnect()
    }
  }, [gridColor, gridSize, glowColors, glowCount])

  return (
    <div className={`relative w-full ${className}`} style={{ backgroundColor }}>
      <canvas ref={canvasRef} className="absolute inset-0 z-0 h-full w-full opacity-15 pointer-events-none" />
      <div className="relative z-10">{children}</div>
    </div>
  )
}

export default GridGlowBackground
